import { useQuery } from '@ltvco/refresh-lib/vendors';
import { useState, useCallback, useRef } from 'react';

import {
  USPTO_API_CONFIG,
  PATENT_SEARCH_FIELDS,
  SORT_FIELD_MAP,
} from './constants';

import type {
  PatentDocument,
  PatentDocumentsResponse,
  PatentSearchFilters,
  PatentSearchHydrationState,
  PatentSearchRequest,
  PatentSearchResponse,
} from './types';

const getUsptoHeaders = () => {
  const apiKey = import.meta.env.VITE_USPTO_API_KEY;
  if (!apiKey) {
    throw new Error('USPTO API key is not configured.');
  }
  return { 'X-API-Key': apiKey };
};

const DEFAULT_PATENT_FILTERS: PatentSearchFilters = {
  status: '',
  applicationType: '',
  applicant: '',
  inventor: '',
  filingDateFrom: '',
  filingDateTo: '',
  grantDateFrom: '',
  grantDateTo: '',
};

const DEFAULT_SORT_OPTION = 'filingDate_desc';

const normalizeSortOption = (value: string): string => {
  return SORT_FIELD_MAP[value] ? value : DEFAULT_SORT_OPTION;
};

const normalizeFilters = (
  filters?: Partial<PatentSearchFilters>
): PatentSearchFilters => {
  return {
    status: filters?.status?.trim() || '',
    applicationType: filters?.applicationType?.trim() || '',
    applicant: filters?.applicant?.trim() || '',
    inventor: filters?.inventor?.trim() || '',
    filingDateFrom: filters?.filingDateFrom || '',
    filingDateTo: filters?.filingDateTo || '',
    grantDateFrom: filters?.grantDateFrom || '',
    grantDateTo: filters?.grantDateTo || '',
  };
};

async function searchPatents(
  params: PatentSearchRequest
): Promise<PatentSearchResponse> {
  const url = `${USPTO_API_CONFIG.BASE_URL}/search`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...getUsptoHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (response.status === 404) {
    return {
      patentFileWrapperDataBag: [],
      count: 0,
    } as PatentSearchResponse;
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(
      `USPTO API error: ${response.status} ${response.statusText}${
        errorText ? ` - ${errorText}` : ''
      }`
    );
  }

  return response.json();
}

const normalizeApplicationNumber = (applicationNumberText: string): string => {
  return applicationNumberText.replace(/\D/g, '');
};

export const getPatentDownloadProxyUrl = (downloadUrl: string): string => {
  if (!downloadUrl) {
    return '';
  }

  if (downloadUrl.startsWith('/api/')) {
    return `/ms/pixie/uspto-api${downloadUrl}`;
  }

  try {
    const parsedUrl = new URL(downloadUrl);
    if (parsedUrl.pathname.startsWith('/api/')) {
      return `/ms/pixie/uspto-api${parsedUrl.pathname}${parsedUrl.search}`;
    }
  } catch {
    return downloadUrl;
  }

  return downloadUrl;
};

export async function openPatentDownloadViaProxy(
  downloadUrl: string
): Promise<void> {
  const proxyUrl = getPatentDownloadProxyUrl(downloadUrl);
  if (!proxyUrl) {
    return;
  }

  const response = await fetch(proxyUrl, {
    headers: getUsptoHeaders(),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(
      `USPTO download error: ${response.status} ${response.statusText}${
        errorText ? ` - ${errorText}` : ''
      }`
    );
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export async function getPatentDocuments(
  applicationNumberText: string
): Promise<PatentDocumentsResponse> {
  const normalizedApplicationNumber = normalizeApplicationNumber(
    applicationNumberText
  );

  if (!normalizedApplicationNumber) {
    throw new Error('Missing application number for document lookup');
  }

  const url = `${USPTO_API_CONFIG.BASE_URL}/${normalizedApplicationNumber}/documents`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getUsptoHeaders(),
  });

  if (response.status === 403) {
    throw new Error('Documents are not available for this patent application.');
  }

  if (response.status === 404) {
    return {
      documentBag: [],
      count: 0,
    } as PatentDocumentsResponse;
  }

  if (!response.ok) {
    throw new Error(
      'Unable to load documents for this patent application. Please try again later.'
    );
  }

  const result = (await response.json()) as PatentDocumentsResponse;
  return {
    ...result,
    documentBag: Array.isArray(result.documentBag)
      ? (result.documentBag as PatentDocument[])
      : [],
  };
}

export function usePatentDocuments(
  applicationNumberText?: string,
  enabled: boolean = true
) {
  const normalizedApplicationNumber = applicationNumberText
    ? normalizeApplicationNumber(applicationNumberText)
    : '';

  const query = useQuery(
    ['patent-documents', normalizedApplicationNumber],
    () => getPatentDocuments(normalizedApplicationNumber),
    {
      enabled: enabled && normalizedApplicationNumber.length > 0,
      staleTime: USPTO_API_CONFIG.STALE_TIME,
      cacheTime: USPTO_API_CONFIG.CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    }
  );

  return {
    documents: query.data?.documentBag ?? [],
    totalCount: query.data?.count ?? 0,
    requestIdentifier: query.data?.requestIdentifier ?? null,
    isLoading: query.isLoading || (query.isFetching && !query.data),
    isFetching: query.isFetching,
    error: query.error instanceof Error ? query.error.message : null,
  };
}

export function usePatentSearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState(DEFAULT_SORT_OPTION);
  const [filters, setFilters] = useState<PatentSearchFilters>(
    DEFAULT_PATENT_FILTERS
  );
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchExecuted, setSearchExecuted] = useState(false);
  const searchTriggerRef = useRef(0);

  const buildRequestBody = useCallback((): PatentSearchRequest => {
    const sortConfig =
      SORT_FIELD_MAP[sortOption] ?? SORT_FIELD_MAP[DEFAULT_SORT_OPTION];

    const structuredFilters = [
      {
        name: 'applicationMetaData.applicationStatusDescriptionText',
        value: filters.status,
      },
      {
        name: 'applicationMetaData.applicationTypeLabelName',
        value: filters.applicationType,
      },
      {
        name: 'applicationMetaData.firstApplicantName',
        value: filters.applicant,
      },
      {
        name: 'applicationMetaData.inventorBag.inventorNameText',
        value: filters.inventor,
      },
    ]
      .filter((item) => item.value.trim().length > 0)
      .map((item) => ({
        name: item.name,
        value: [item.value],
      }));

    const rangeFilters = [
      filters.filingDateFrom && filters.filingDateTo
        ? {
            field: 'applicationMetaData.filingDate',
            valueFrom: filters.filingDateFrom,
            valueTo: filters.filingDateTo,
          }
        : null,
      filters.grantDateFrom && filters.grantDateTo
        ? {
            field: 'applicationMetaData.grantDate',
            valueFrom: filters.grantDateFrom,
            valueTo: filters.grantDateTo,
          }
        : null,
    ].filter(
      (item): item is { field: string; valueFrom: string; valueTo: string } =>
        item !== null
    );

    return {
      q: searchQuery,
      fields: [...PATENT_SEARCH_FIELDS],
      sort: [sortConfig],
      filters: structuredFilters.length > 0 ? structuredFilters : undefined,
      rangeFilters: rangeFilters.length > 0 ? rangeFilters : undefined,
      pagination: {
        offset: currentOffset,
        limit: USPTO_API_CONFIG.DEFAULT_PAGE_SIZE,
      },
    };
  }, [searchQuery, sortOption, filters, currentOffset]);

  const queryKey = [
    'patent-search',
    searchQuery,
    sortOption,
    filters.status,
    filters.applicationType,
    filters.applicant,
    filters.inventor,
    filters.filingDateFrom,
    filters.filingDateTo,
    filters.grantDateFrom,
    filters.grantDateTo,
    currentOffset,
    searchTriggerRef.current,
  ];

  const query = useQuery(queryKey, () => searchPatents(buildRequestBody()), {
    enabled: searchExecuted && searchQuery.trim().length > 0,
    staleTime: USPTO_API_CONFIG.STALE_TIME,
    cacheTime: USPTO_API_CONFIG.CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
    onSettled: () => {
      setSearchExecuted(false);
    },
  });

  const hydrateFromUrl = useCallback((state: PatentSearchHydrationState) => {
    const normalizedQuery = state.query.trim();
    const normalizedSort = normalizeSortOption(state.sortOption);
    const normalizedPage =
      Number.isFinite(state.page) && state.page > 0
        ? Math.floor(state.page)
        : 1;
    const normalizedOffset =
      (normalizedPage - 1) * USPTO_API_CONFIG.DEFAULT_PAGE_SIZE;

    setSearchQuery(normalizedQuery);
    setSortOption(normalizedSort);
    setFilters(normalizeFilters(state.filters));
    setCurrentOffset(normalizedOffset);

    if (normalizedQuery.length > 0) {
      searchTriggerRef.current += 1;
      setSearchExecuted(true);
    } else {
      setSearchExecuted(false);
    }
  }, []);

  const triggerSearch = useCallback(
    (
      newQuery: string,
      newFilters?: Partial<PatentSearchFilters>,
      newPage: number = 1,
      newSortOption?: string
    ) => {
      const normalizedQuery = newQuery.trim();
      const normalizedPage =
        Number.isFinite(newPage) && newPage > 0 ? Math.floor(newPage) : 1;

      setSearchQuery(normalizedQuery);
      if (newFilters) {
        setFilters(normalizeFilters(newFilters));
      }
      if (newSortOption) {
        setSortOption(normalizeSortOption(newSortOption));
      }
      setCurrentOffset(
        (normalizedPage - 1) * USPTO_API_CONFIG.DEFAULT_PAGE_SIZE
      );
      searchTriggerRef.current += 1;
      setSearchExecuted(normalizedQuery.length > 0);
    },
    []
  );

  const handleNextPage = useCallback(() => {
    setCurrentOffset((prev) => prev + USPTO_API_CONFIG.DEFAULT_PAGE_SIZE);
    setSearchExecuted(true);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setCurrentOffset((prev) =>
      Math.max(0, prev - USPTO_API_CONFIG.DEFAULT_PAGE_SIZE)
    );
    setSearchExecuted(true);
  }, []);

  const results = query.data ?? null;
  const totalCount = results?.count ?? 0;
  const hasNextPage =
    currentOffset + USPTO_API_CONFIG.DEFAULT_PAGE_SIZE < totalCount;
  const hasPreviousPage = currentOffset > 0;
  const currentPage =
    Math.floor(currentOffset / USPTO_API_CONFIG.DEFAULT_PAGE_SIZE) + 1;
  const totalPages = Math.ceil(totalCount / USPTO_API_CONFIG.DEFAULT_PAGE_SIZE);

  return {
    results,
    isSearching: query.isFetching,
    hasResults: !!results && totalCount > 0,
    generalError: query.error instanceof Error ? query.error.message : null,
    searchQuery,
    appliedFilters: filters,
    sortOption,
    hydrateFromUrl,
    setSortOption: (value: string) => {
      setSortOption(normalizeSortOption(value));
      if (searchQuery.trim()) {
        setCurrentOffset(0);
        setSearchExecuted(true);
      }
    },
    triggerSearch,
    handleNextPage,
    handlePreviousPage,
    hasNextPage,
    hasPreviousPage,
    currentPage,
    totalPages,
    totalCount,
  };
}
